import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import {
  Vendor,
  VendorCategory,
  VendorCreatePayload,
  VendorListParams,
  VendorUpdatePayload,
  VendorsPage,
} from 'src/models/vendor.model';

@Injectable({ providedIn: 'root' })
export class Vendors {
  private http = inject(HttpClient);
  private baseUrl = `${environment.api_url}api/v1/customer/vendors`;

  listVendors(params: VendorListParams = {}): Observable<VendorsPage> {
    return this.http
      .get<Vendor[]>(this.baseUrl, {
        observe: 'response',
        params: this.buildListParams(params),
      })
      .pipe(map(response => this.toPage(response, params)));
  }

  getVendors(): Observable<Vendor[]> {
    return this.http.get<Vendor[]>(this.baseUrl);
  }

  getVendor(id: number): Observable<Vendor> {
    return this.http.get<Vendor>(`${this.baseUrl}/${id}`);
  }

  createVendor(payload: VendorCreatePayload): Observable<Vendor> {
    return this.http.post<Vendor>(this.baseUrl, payload);
  }

  updateVendor(id: number, payload: VendorUpdatePayload): Observable<Vendor> {
    return this.http.put<Vendor>(`${this.baseUrl}/${id}`, payload);
  }

  // 404 on delete is treated as success so optimistic UI removals don't
  // surface confusing errors when the row is already gone.
  deleteVendor(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      catchError((err: HttpErrorResponse) =>
        err.status === 404 ? of(void 0) : throwError(() => err)
      )
    );
  }

  getVendorCategories(): Observable<VendorCategory[]> {
    return this.http.get<VendorCategory[]>(`${environment.api_url}api/v1/customer/vendor-categories`);
  }

  private buildListParams(params: VendorListParams): HttpParams {
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 1))
      .set('per_page', String(params.perPage ?? 25));
    const term = params.search?.trim();
    if (term) {
      httpParams = httpParams.set('search', term);
    }
    return httpParams;
  }

  private toPage(response: HttpResponse<Vendor[]>, params: VendorListParams): VendorsPage {
    const headers = response.headers;
    const body = response.body ?? [];
    return {
      vendors: body,
      totalCount: Number(headers.get('X-Total-Count')) || body.length,
      totalPages: Number(headers.get('X-Total-Pages')) || 1,
      currentPage: Number(headers.get('X-Current-Page')) || params.page || 1,
      perPage: Number(headers.get('X-Per-Page')) || params.perPage || 25,
    };
  }
}
